import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
jQuery(document).ready(function($) {
    setTimeout(function() {
        $('.accordion--AeFROa3V3RjVMRUxkZ__accordion_tyWare').appendTo('.product-information__media');
    }, 100);
});




// jQuery(document).ready(function($) {
//   function triggerNativeClick(el) {
//     if (!el) return;
//     const event = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
//     el.dispatchEvent(event);
//   }
//   function selectShopifyOption(type) {
//     const $block = $('.shopify_subscriptions_app_block').first();
//     if (!$block.length) {
//       console.warn('[purchase-options] No Shopify subscription block found.');
//       return;
//     }
//     if (type === 'one_time') {
//       const $oneTime = $block.find('input.shopify_subscriptions_app_block_one_time_purchase_option').first();
//       if ($oneTime.length) {
//         console.log('[purchase-options] Triggering ONE TIME PURCHASE');
//         $oneTime.prop('checked', true);
//         triggerNativeClick($oneTime[0]); // Fire Shopify’s native handler
//       }
//     } else if (type === 'subscribe') {
//       const $sellingPlan = $block.find('input[data-radio-type="selling_plan"]').first();
//       if ($sellingPlan.length) {
//         console.log('[purchase-options] Triggering SUBSCRIBE AND SAVE');
//         $sellingPlan.prop('checked', true);
//         triggerNativeClick($sellingPlan[0]); // Fire Shopify’s native handler
//       }
//     }
//   }
//   $('.purchase-options input[name="purchase-option"]').on('change', function() {
//     const $option = $(this).closest('.option');
//     const title = $option.find('.option-title').text().trim().toLowerCase();

//     $('.purchase-options .option').removeClass('active');
//     $option.addClass('active');

//     if (title.includes('one time')) {
//       selectShopifyOption('one_time');
//     } else if (title.includes('subscribe')) {
//       selectShopifyOption('subscribe');
//     }
//   });
//   // On load, sync the initially checked option
//   const $initial = $('.purchase-options input[name="purchase-option"]:checked').closest('.option');
//   if ($initial.length) {
//     const title = $initial.find('.option-title').text().trim().toLowerCase();
//     if (title.includes('one time')) {
//       selectShopifyOption('one_time');
//     } else if (title.includes('subscribe')) {
//       selectShopifyOption('subscribe');
//     }
//   }
// });


// jQuery(document).ready(function($) {
//   let interval = setInterval(function () {
//     let $subscriptionRadio = $('input[data-radio-type="selling_plan"]').first();
//     if ($subscriptionRadio.length) {
//       clearInterval(interval);
//       $subscriptionRadio.prop("checked", true).trigger("change").trigger("click");
//       let sellingPlanId = $subscriptionRadio.data("selling-plan-id");
//       $('.selected-selling-plan-id').val(sellingPlanId);
//       console.log("Subscription default selected:", sellingPlanId);
//     }

//   }, 200);
 
//   $(document).on("change", ".variant-picker__form input[type='radio']", function () {
//       // Get selected variant ID
//       let variantId = $(this).data("variant-id");
//       if (!variantId) return;
//       // Hide all subscription sections
//       $(".shopify_subscriptions_app_block").addClass("shopify_subscriptions_app_block--hidden");
//       // Show only the selected variant's section
//       let selectedSection = $(".shopify_subscriptions_app_block[data-variant-id='" + variantId + "']");
//       selectedSection.removeClass("shopify_subscriptions_app_block--hidden");

//       // Auto-select the subscription radio button inside that section
//       let subscribeRadio = selectedSection.find("input[data-radio-type='selling_plan']");
//       if (subscribeRadio.length > 0) {
//           subscribeRadio.prop("checked", true).trigger("change");
//       }
//   });
//   $('input[name="purchase-option"]').eq(0).on('change', function () {
//         if ($(this).is(':checked')) {
//             // Select Shopify "Deliver every month"
//             let sellingPlan = $('.shopify_subscriptions_app_block input[data-radio-type="selling_plan"]').first();
//             sellingPlan.prop('checked', true).trigger('change').click();

//         }

//     });
 
//     // Custom "One-time Purchase"

//     $('input[name="purchase-option"]').eq(1).on('change', function () {

//         if ($(this).is(':checked')) {
 
//             // Select Shopify "One-time purchase"

//             let oneTime = $('.shopify_subscriptions_app_block input[data-radio-type="one_time_purchase"]').first();

//             oneTime.prop('checked', true).trigger('change').click();

//         }

//     });
 
 
// });

 
// document.addEventListener("DOMContentLoaded", function () {
//   // Variation radios (bundle radios)
//   const bundleRadios = document.querySelectorAll(".variant-picker__form input[type='radio']");
//   // Your custom purchase option radios
//   const purchaseOptionRadios = document.querySelectorAll("input[name='purchase-option']");
//   // Hidden Shopify selling_plan field
//   const sellingPlanField = document.querySelector(".selected-selling-plan-id");
 
//   function showSubscriptionBlock(variantId) {
//     document.querySelectorAll(".shopify_subscriptions_app_block").forEach(section => {
//       if (section.dataset.variantId === variantId) {
//         section.classList.remove("shopify_subscriptions_app_block--hidden");
//       } else {
//         section.classList.add("shopify_subscriptions_app_block--hidden");
//       }
//     });
//   }
 
//   function syncSubscriptionSelection(variantId) {
//     const block = document.querySelector(`.shopify_subscriptions_app_block[data-variant-id="${variantId}"]`);
//     if (!block) return;
 
//     const oneTimeInput = block.querySelector("input[data-radio-type='one_time_purchase']");
//     const subscribeInput = block.querySelector("input[data-radio-type='selling_plan']");
 
//     if (purchaseOptionRadios[0].checked) {
//       // Subscribe selected
//       subscribeInput.checked = true;
//       sellingPlanField.value = subscribeInput.dataset.sellingPlanId;
//     } else {
//       // One-time selected
//       oneTimeInput.checked = true;
//       sellingPlanField.value = "";
//     }
//   }
 
//   // When bundle is changed
//   bundleRadios.forEach(radio => {
//     radio.addEventListener("change", function () {
//       const variantId = this.dataset.variantId;
//       // Update hidden variant field
//       document.querySelector("input[name='id']").value = variantId;
 
//       // Show the matching subscription block
//       showSubscriptionBlock(variantId);
 
//       // Sync subscription state
//       syncSubscriptionSelection(variantId);
//     });
//   });
 
//   // When purchase option is changed
//   purchaseOptionRadios.forEach(option => {
//     option.addEventListener("change", function () {
//       const variantId = document.querySelector("input[name='id']").value;
//       syncSubscriptionSelection(variantId);
//     });
//   });
 
// });

jQuery(document).ready(function ($) {

  const bundleRadios = $(".variant-picker__form input[type='radio']");
  const purchaseOptionRadios = $("input[name='purchase-option']");
  const sellingPlanField = $(".selected-selling-plan-id");

  function showSubscriptionBlock(variantId) {
    $(".shopify_subscriptions_app_block").each(function () {
      if ($(this).data("variant-id") == variantId) {
        $(this).removeClass("shopify_subscriptions_app_block--hidden");
      } else {
        $(this).addClass("shopify_subscriptions_app_block--hidden");
      }
    });
  }

  function syncSubscriptionSelection(variantId) {
    const block = $(`.shopify_subscriptions_app_block[data-variant-id="${variantId}"]`);
    if (!block.length) return;

    const oneTimeInput = block.find("input[data-radio-type='one_time_purchase']");
    const subscribeInput = block.find("input[data-radio-type='selling_plan']");

    if (purchaseOptionRadios.eq(0).is(":checked")) {
      subscribeInput.prop("checked", true).trigger("change").trigger("click");
      sellingPlanField.val(subscribeInput.data("selling-plan-id"));
    } else {
      oneTimeInput.prop("checked", true).trigger("change").trigger("click");
      sellingPlanField.val("");
    }
  }

  // When variant (bundle) changes
  bundleRadios.on("change", function () {
    const variantId = $(this).data("variant-id");

    $("input[name='id']").val(variantId);
    showSubscriptionBlock(variantId);
    syncSubscriptionSelection(variantId);
  });

  // When purchase option (subscribe/one-time) changes
  purchaseOptionRadios.on("change", function () {
    const variantId = $("input[name='id']").val();
    syncSubscriptionSelection(variantId);
  });

  // -----------------------------------------
  // ⭐ DEFAULT: Subscribe & Save Auto-Selected
  // -----------------------------------------
  setTimeout(function () {

    // Select first "Subscribe & Save" radio inside subscription blocks
    let $subscribeRadio = $("input[data-radio-type='selling_plan']").first();

    if ($subscribeRadio.length) {
      $subscribeRadio.prop("checked", true).trigger("change").trigger("click");

      sellingPlanField.val($subscribeRadio.data("selling-plan-id"));
    }

    // Update your custom radio group
    $("input[name='purchase-option'][value='subscribe']").prop("checked", true).trigger("change");

    // Select correct subscription block for first variant
    let defaultVariantId = $("input[name='id']").val();
    showSubscriptionBlock(defaultVariantId);


var onetime_price_sub = $("section.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden) .shopify_subscriptions_app_block_label.shopify_subscriptions_purchase_option_wrapper .shopify_subscriptions_in_widget_price").text();
var subscription_price = $("section.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden) .shopify_subscriptions_purchase_option_wrapper .shopify_subscriptions_in_widget_price.allocation_price").text();        
$("strong.append_subcription_price").text('Price:'+subscription_price);
        $("span.old-price.subscription-old-price").text(onetime_price_sub);
        $("strong.append_ontime_price").text('Price:'+onetime_price_sub);


  }, 500);





$(".variant-picker__grid label.variant-card").each(function(){
$(this).click(function(){
    setTimeout(function() {
   var onetime_price_sub = $("section.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden) .shopify_subscriptions_app_block_label.shopify_subscriptions_purchase_option_wrapper .shopify_subscriptions_in_widget_price").text();
var subscription_price = $("section.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden) .shopify_subscriptions_purchase_option_wrapper .shopify_subscriptions_in_widget_price.allocation_price").text();        
$("strong.append_subcription_price").text('Price:'+subscription_price);
        $("span.old-price.subscription-old-price").text(onetime_price_sub);
        $("strong.append_ontime_price").text('Price:'+onetime_price_sub);
        }, 300); 
        
      
});
    
});



});
